import { type FC, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { usePutPortfolioManualValueMutation } from "web_myplann/Api/Portfolio/Mutations/usePutPortfolioManualValueMutation";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import { usePortfolioQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioQuery";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioDetailMenu } from "web_myplann/Portfolio/Components/PortfolioDetailMenu";
import { DetailEmptyState } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailEmptyState";
import { DetailEventAlertsSection } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailEventAlertsSection";
import { DetailLayout } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailLayout";
import { DetailRemindersSection } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRemindersSection";
import { PORTFOLIO_SECTION_BY_DOMAIN } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/portfolioSectionRegistry";
import { DetailSkeleton } from "web_myplann/Portfolio/Components/Redesign/Detail/Skeletons/DetailSkeleton";
import { InvestmentValueDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/Value/InvestmentValueDrawer";
import { getReminderEntity } from "web_myplann/Portfolio/Helpers/getReminderEntity";
import { hasNoBusinessData } from "web_myplann/Portfolio/Helpers/hasNoBusinessData";
import { isInvestmentDomain } from "web_myplann/Portfolio/Helpers/isInvestmentDomain";
import { portfolioHasChart } from "web_myplann/Portfolio/Helpers/portfolioHasChart";
import { usePortfolioTitle } from "web_myplann/Portfolio/Hooks/usePortfolioTitle";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";

export const PortfolioDetailPageRedesign: FC = () => {
	const { id } = useParams<{ id: string }>();
	const portfolioId = id ? Number(id) : undefined;
	const { t } = useResource();
	const location = useLocation();
	const stateDomain = (location.state as { domain?: ClientPortfolioDomainEnum })
		?.domain;
	const [isValueDrawerOpen, setIsValueDrawerOpen] = useState(false);

	const { data: portfolio, isLoading } = usePortfolioQuery(Number(portfolioId));

	const title = usePortfolioTitle(portfolio, t);

	const currencySign =
		portfolio?.currencySign ?? t(Resources.Common.CurrencySign);

	const domain = portfolio?.domain ?? undefined;

	const showEmpty = !isLoading && hasNoBusinessData(portfolio);

	const DomainDetail =
		domain !== undefined && !showEmpty
			? PORTFOLIO_SECTION_BY_DOMAIN[domain]
			: undefined;

	const showUpdateValue =
		isInvestmentDomain(domain) ||
		domain === ClientPortfolioDomainEnum.BankingProducts;
	const { data: reporting } = usePortfolioIdReportingQuery(
		showUpdateValue ? portfolioId : undefined,
	);
	const { mutate: saveValue, isPending: isSavingValue } =
		usePutPortfolioManualValueMutation(Number(portfolioId));

	const openValueDrawer = showUpdateValue
		? () => setIsValueDrawerOpen(true)
		: undefined;

	return (
		<DetailLayout
			title={title}
			menu={
				domain !== undefined && portfolioId !== undefined ? (
					<PortfolioDetailMenu
						kind={PortfolioItemKind.Portfolio}
						id={portfolioId}
						domain={domain}
						title={title}
						onUpdateValue={openValueDrawer}
					/>
				) : undefined
			}
			hero={
				portfolio && DomainDetail ? (
					<DetailEventAlertsSection portfolio={portfolio} />
				) : undefined
			}
		>
			{isLoading ? (
				<DetailSkeleton hasChart={portfolioHasChart(stateDomain)} />
			) : showEmpty ? (
				<DetailEmptyState />
			) : portfolio && DomainDetail ? (
				<DomainDetail
					baseData={portfolio}
					currencySign={currencySign}
					onUpdateValue={openValueDrawer}
				/>
			) : null}

			{!isLoading && portfolio && !DomainDetail && (
				<DetailRemindersSection
					entity={getReminderEntity(PortfolioItemKind.Portfolio, portfolio.id)}
				/>
			)}

			{showUpdateValue && portfolioId !== undefined && (
				<InvestmentValueDrawer
					open={isValueDrawerOpen}
					onClose={() => setIsValueDrawerOpen(false)}
					subtitle={title}
					defaultInvested={reporting?.summary?.totalInvestedValue ?? null}
					previousValue={reporting?.summary?.totalValue ?? null}
					previousValueDate={portfolio?.portfolioValueDate ?? null}
					currencySign={currencySign}
					isPending={isSavingValue}
					onSave={(request, onSuccess) => saveValue(request, { onSuccess })}
				/>
			)}
		</DetailLayout>
	);
};
